import { useCallback, useMemo, useState } from "react"
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  TextField,
  Typography,
} from "@mui/material"
import { OK, ERROR } from "helpers/data-statuses"
import { useLayouts, type Layout, type LayoutOwnerType } from "resources/layouts"
import { useIsLayoutAccountShareEnabled } from "resources/account"
import { useCreateDashboard } from "../../hooks/use-create-dashboard"
import { useCanManageLayouts } from "../../hooks/use-can-manage-layouts"
import { useDialogFormState } from "../../hooks/use-dialog-form-state"

export interface NewDashboardDialogProps {
  open: boolean
  onClose: () => void
}

// Tier display order + labels; "op" rows surface first as Templates. "Account Layouts" is the house term for the account tier. (APP-2690)
const TIER_ORDER: { ownerType: LayoutOwnerType, label: string }[] = [
  { ownerType: "op", label: "Templates" },
  { ownerType: "user", label: "Your layouts" },
  { ownerType: "account", label: "Account Layouts" },
]

interface LayoutGroup {
  ownerType: LayoutOwnerType
  label: string
  layouts: Layout[]
}

function groupByTier(layouts: Layout[]): LayoutGroup[] {
  return TIER_ORDER
    .map(({ ownerType, label }) => ({
      ownerType,
      label,
      layouts: layouts.filter((layout) => layout.ownerType === ownerType),
    }))
    .filter((group) => group.layouts.length > 0)
}

export default function NewDashboardDialog({ open, onClose }: NewDashboardDialogProps) {
  // Hold the fetch until the dialog is actually open — it mounts unconditionally alongside the
  // switcher, so an eager fetch here would fire for every multi-dashboard user's initial load. [APP-3309]
  const [layouts, status] = useLayouts(open)
  const { createDashboard, createFromOwnLayout, isContentSetsLoaded, layoutsStatus } = useCreateDashboard(open)
  const canManageLayouts = useCanManageLayouts()
  const isLayoutAccountShareEnabled = useIsLayoutAccountShareEnabled()
  // Picker visibility only — separate from which create path Create takes (usesPickerPath, below). (APP-2690)
  const canSeePicker = canManageLayouts || isLayoutAccountShareEnabled

  const [name, setName] = useState("")
  const [selectedLayoutId, setSelectedLayoutId] = useState<string | null>(null)
  const {
    isBusy: isCreating,
    error,
    beginBusy,
    endBusy,
    reset: resetFormState,
    guardedClose,
  } = useDialogFormState()

  const groups = useMemo(() => (canSeePicker ? groupByTier(layouts ?? []) : []), [canSeePicker, layouts])
  // Manager only — a regular member gets no default highlight unless they explicitly pick. (APP-2690)
  const firstLayoutId = groups[0]?.layouts[0]?.id ?? null
  // Drops a stale explicit pick once canSeePicker withdraws, derived at render time like the save dialog's tier clamp. (APP-2690)
  const effectiveLayoutId = canSeePicker ? (selectedLayoutId ?? (canManageLayouts ? firstLayoutId : null)) : null
  const selectedLayout = useMemo(
    () => (canSeePicker ? (layouts ?? []).find((layout) => layout.id === effectiveLayoutId) ?? null : null),
    [canSeePicker, layouts, effectiveLayoutId],
  )
  // Whether Create takes the picker's copy-based path: always for a manager, only after an explicit pick for a regular member. (APP-2690)
  const usesPickerPath = canManageLayouts || selectedLayout !== null

  const reset = useCallback(() => {
    setName("")
    setSelectedLayoutId(null)
    resetFormState()
  }, [resetFormState])

  const handleClose = useCallback(() => {
    guardedClose(() => { reset(); onClose() })
  }, [guardedClose, reset, onClose])

  const trimmedName = name.trim()
  // Name always required; a source layout only on the picker path — so cancelling early leaves no orphans.
  // The own-layout path (!usesPickerPath) can synthesize a default config from content-set
  // entitlements, which default false while loading — block Create until they're known, or an
  // entitled account can permanently persist a layout missing the thematic-betas panel. [APP-3309]
  // Also blocks until the layouts fetch reaches a terminal state, or a fast click can race it. [APP-3309]
  const isCreateDisabled = trimmedName.length === 0 || isCreating
    || (usesPickerPath && selectedLayout === null)
    || (!usesPickerPath && (!isContentSetsLoaded || (layoutsStatus !== OK && layoutsStatus !== ERROR)))

  const handleCreate = useCallback(async () => {
    if (trimmedName.length === 0) return
    if (usesPickerPath && selectedLayout === null) return
    if (!beginBusy()) return
    try {
      // Picker path vs own-layout path: a manager always uses the picker; a member only after an explicit pick, else createFromOwnLayout. [APP-3284]
      if (usesPickerPath && selectedLayout) {
        await createDashboard(trimmedName, selectedLayout)
      } else {
        await createFromOwnLayout(trimmedName)
      }
      reset()
      onClose()
    } catch {
      // Keep the dialog open (with the chosen name/layout intact) so the user can retry. [APP-3309]
      endBusy("Could not create a new dashboard. Please try again.")
    }
  }, [trimmedName, usesPickerPath, selectedLayout, beginBusy, createDashboard, createFromOwnLayout, reset, onClose, endBusy])

  // The layouts list degrades in-modal (spinner/inline error); neither blocks closing.
  const isListLoading = !(status === OK || status === ERROR)
  const isListError = status === ERROR

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>New dashboard</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <TextField
          autoFocus
          required
          fullWidth
          margin="dense"
          label="Dashboard name"
          helperText="What you'll call this dashboard."
          value={name}
          onChange={(event) => { setName(event.target.value) }}
          disabled={isCreating}
        />
        {canSeePicker && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1.5 }}>Starting layout</Typography>
            <Typography variant="caption" color="text.secondary">
              The panels and arrangement your new dashboard opens with.
              {!canManageLayouts && " Defaults to a copy of your current dashboard if you don't pick one."}
            </Typography>
            {isListLoading && (
              <CircularProgress aria-label="Loading layouts" size={24} sx={{ display: "block", mx: "auto", my: 2 }} />
            )}
            {isListError && (
              <Alert severity="error" sx={{ my: 2 }}>Could not load starting points. Please try again.</Alert>
            )}
            {!isListLoading && !isListError && groups.length === 0 && (
              <Alert severity="info" sx={{ my: 2 }}>No starting points are available yet.</Alert>
            )}
            {!isListLoading && !isListError && groups.map((group) => (
              <List
                key={group.ownerType}
                dense
                subheader={<ListSubheader disableSticky>{group.label}</ListSubheader>}
              >
                {group.layouts.map((layout) => (
                  <ListItemButton
                    key={layout.id}
                    selected={layout.id === effectiveLayoutId}
                    disabled={isCreating}
                    onClick={() => { setSelectedLayoutId(layout.id) }}
                  >
                    <ListItemText primary={layout.name} />
                  </ListItemButton>
                ))}
              </List>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isCreating}>Cancel</Button>
        <Button onClick={() => { void handleCreate() }} disabled={isCreateDisabled} variant="contained">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  )
}
